import {
  useCategories,
  useIsLoadingCategories,
  useLoadCategories,
} from "@/stores/category";
import { parseSearchParams } from "@/utils/parse";
import { Box, Button, Checkbox, Flex, Menu, Text } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import { IconChevronDown } from "@tabler/icons-react";
import type { DiscoverFilterValues } from "@/types/features/discover";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import "./DiscoverFilterCategory.css";

export function DiscoverFilterCategory({
  form,
  onSubmit,
}: {
  form: UseFormReturnType<DiscoverFilterValues>;
  onSubmit: (values: DiscoverFilterValues) => void;
}) {
  const [opened, setOpened] = useState(false);
  const [searchParams] = useSearchParams();
  const categories = useCategories();
  const isLoadingCategories = useIsLoadingCategories();
  const loadCategories = useLoadCategories();

  // Read filter values directly from search params for labels
  const queryValues = useMemo(() => parseSearchParams(searchParams), [searchParams]);
  const queryCategories = Array.isArray(queryValues.category)
    ? queryValues.category
    : [];

  const selectedCategories = form.values.category || [];

  const initialCategoriesRef = useRef<string[]>(
    Array.isArray(form.values.category) ? form.values.category : []
  );

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const prevOpenedRef = useRef(opened);

  useEffect(() => {
    if (opened && !prevOpenedRef.current) {
      initialCategoriesRef.current = Array.isArray(form.values.category)
        ? form.values.category
        : [];
    }
    prevOpenedRef.current = opened;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const isFormDirty = useMemo(() => {
    const initialCategories = Array.isArray(initialCategoriesRef.current)
      ? initialCategoriesRef.current
      : [];
    const currentCategories = Array.isArray(form.values.category)
      ? form.values.category
      : [];

    if (initialCategories.length !== currentCategories.length) {
      return true;
    }

    return !initialCategories.every((category: string) =>
      currentCategories.includes(category)
    );
  }, [form.values.category]);

  const handleCategoryToggle = (categoryId: string) => {
    const current = form.values.category || [];
    const newCategories = current.includes(categoryId)
      ? current.filter((c) => c !== categoryId)
      : [...current, categoryId];
    form.setFieldValue("category", newCategories);
  };

  const handleApply = () => {
    onSubmit(form.values);
    initialCategoriesRef.current = Array.isArray(form.values.category)
      ? form.values.category
      : [];
    setOpened(false);
  };

  const handleReset = () => {
    form.setFieldValue("category", undefined);
    initialCategoriesRef.current = [];
    onSubmit({ ...form.values, category: undefined });
    setOpened(false);
  };

  return (
    <Menu
      width={350}
      position="bottom-start"
      opened={opened}
      onChange={setOpened}
      closeOnItemClick={false}
    >
      <Menu.Target>
        <Button
          size="xs"
          variant="subtle"
          rightSection={
            <IconChevronDown
              color={
                queryCategories.length > 0
                  ? "var(--mantine-color-eu_flag-9)"
                  : "var(--mantine-color-gray-9)"
              }
              size={12}
            />
          }
        >
          <Text
            fz={15}
            c={queryCategories.length > 0 ? "eu_flag.9" : "gray.9"}
            fw={queryCategories.length > 0 ? 600 : 400}
          >
            Category
            {queryCategories.length > 0 && ` (${queryCategories.length})`}
          </Text>
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Flex direction="column" p="xs" gap="sm">
          <Text fz={14} fw={500}>
            Categories
          </Text>
        </Flex>
        {isLoadingCategories ? (
          <Menu.Item disabled>
            <Text>Loading categories...</Text>
          </Menu.Item>
        ) : (
          <Box className="category-list">
            {categories.map((category) => (
              <Menu.Item key={category.category_id}>
                <Checkbox
                  label={category.category_name_english}
                  checked={selectedCategories.includes(category.category_id)}
                  onChange={() => handleCategoryToggle(category.category_id)}
                />
              </Menu.Item>
            ))}
          </Box>
        )}
        <Flex direction="column" p="xs" gap="sm">
          <Button
            fullWidth
            mt="sm"
            onClick={handleApply}
            disabled={!isFormDirty}
          >
            Apply
          </Button>
          <Button fullWidth variant="subtle" color="gray" onClick={handleReset}>
            Reset
          </Button>
        </Flex>
      </Menu.Dropdown>
    </Menu>
  );
}
